import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Compass, Brain, Rocket, Target, BarChart3, Briefcase, X } from "lucide-react";

function ConfirmModal({ onConfirm, onCancel }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 lg:left-64 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0, 51, 38, 0.6)" }}
      onClick={onCancel}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 10 }}
        transition={{ duration: 0.2 }}
        className="bg-white rounded-2xl w-full max-w-md overflow-hidden"
        style={{ boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          {/* Header */}
          <div className="flex items-start justify-between mb-5">
            <h2
              className="text-lg font-bold"
              style={{ fontFamily: "Poppins, sans-serif", color: "#003326" }}
            >
              Antes de começar
            </h2>
            <button
              onClick={onCancel}
              className="p-1 rounded-lg hover:bg-gray-100 transition-colors -mr-1 -mt-1"
            >
              <X className="w-4 h-4" style={{ color: "#99A6AB" }} />
            </button>
          </div>

          {/* Content */}
          <div className="space-y-4 mb-6">
            <div
              className="rounded-xl p-4"
              style={{ background: "#EAFAF6" }}
            >
              <p
                className="text-sm font-semibold mb-1"
                style={{ fontFamily: "Work Sans, sans-serif", color: "#00664C" }}
              >
                Reserve pelo menos 10 minutos
              </p>
              <p
                className="text-sm leading-relaxed"
                style={{ fontFamily: "Work Sans, sans-serif", color: "#646C6F" }}
              >
                Encontre um lugar tranquilo, sem interrupções. Respostas feitas com
                pressa ou distração comprometem a precisão do seu diagnóstico.
              </p>
            </div>

            <div
              className="rounded-xl p-4"
              style={{ background: "#FFF8EC", border: "1px solid #F5E6C8" }}
            >
              <p
                className="text-sm font-semibold mb-1"
                style={{ fontFamily: "Work Sans, sans-serif", color: "#8B6914" }}
              >
                Você só poderá refazer em 90 dias
              </p>
              <p
                className="text-sm leading-relaxed"
                style={{ fontFamily: "Work Sans, sans-serif", color: "#646C6F" }}
              >
                O teste vocacional mede padrões estáveis de afinidade profissional. Refazer com
                frequência não muda seu perfil real — só distorce o resultado.
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-2">
            <button
              onClick={() => { onCancel(); onConfirm(); }}
              className="w-full py-3.5 rounded-full text-sm font-semibold transition-all duration-200 hover:shadow-md"
              style={{
                fontFamily: "Work Sans, sans-serif",
                background: "#00DCA4",
                color: "#003326",
              }}
            >
              Estou pronto, iniciar
            </button>
            <button
              onClick={onCancel}
              className="w-full py-3 rounded-full text-sm font-medium transition-colors hover:bg-gray-50"
              style={{
                fontFamily: "Work Sans, sans-serif",
                color: "#99A6AB",
              }}
            >
              Voltar
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function VocationalIntro({ onStart }) {
  const [showConfirm, setShowConfirm] = useState(false);

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
        className="flex flex-col items-center justify-center py-12 px-4"
      >
        <div className="max-w-lg w-full text-center">
          {/* Animated icon cluster */}
          <div className="relative w-28 h-28 mx-auto mb-8">
            {/* Pulse rings */}
            <motion.div
              className="absolute inset-0 rounded-full"
              style={{ border: "1px solid #B0FCEA" }}
              animate={{ scale: [1, 1.5, 1.5], opacity: [0.4, 0, 0] }}
              transition={{ duration: 2.5, repeat: Infinity, ease: "easeOut" }}
            />
            <motion.div
              className="absolute inset-0 rounded-full"
              style={{ border: "1px solid #B0FCEA" }}
              animate={{ scale: [1, 1.5, 1.5], opacity: [0.4, 0, 0] }}
              transition={{ duration: 2.5, repeat: Infinity, ease: "easeOut", delay: 0.8 }}
            />

            {/* Center icon */}
            <motion.div
              className="absolute inset-0 flex items-center justify-center"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div
                className="w-16 h-16 rounded-2xl flex items-center justify-center"
                style={{ background: "#EAFAF6", border: "1.5px solid #B0FCEA" }}
              >
                <Compass className="w-7 h-7" style={{ color: "#00DCA4" }} />
              </div>
            </motion.div>

            {/* Orbiting icons */}
            {[
              { Icon: Brain, angle: 0, delay: 0.3 },
              { Icon: Rocket, angle: 60, delay: 0.4 },
              { Icon: Target, angle: 120, delay: 0.5 },
              { Icon: BarChart3, angle: 180, delay: 0.6 },
              { Icon: Briefcase, angle: 240, delay: 0.7 },
            ].map(({ Icon, angle, delay }, i) => {
              const rad = (angle * Math.PI) / 180;
              const r = 50;
              const x = Math.cos(rad) * r;
              const y = Math.sin(rad) * r;
              return (
                <motion.div
                  key={i}
                  className="absolute left-1/2 top-1/2 w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{
                    background: "#fff",
                    border: "1px solid #E5E9EA",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                    marginLeft: -16,
                    marginTop: -16,
                  }}
                  initial={{ x: 0, y: 0, opacity: 0, scale: 0 }}
                  animate={{ x, y, opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay, ease: [0.25, 0.1, 0.25, 1] }}
                >
                  <Icon className="w-3.5 h-3.5" style={{ color: "#00DCA4" }} />
                </motion.div>
              );
            })}
          </div>

          <h1
            className="text-3xl font-bold mb-3"
            style={{ fontFamily: "Poppins, sans-serif", lineHeight: 1.25, color: "#003326" }}
          >
            Descubra sua direção profissional
          </h1>

          <p
            className="text-sm leading-relaxed mb-8"
            style={{ fontFamily: "Work Sans, sans-serif", color: "#646C6F" }}
          >
            Um diagnóstico estruturado de 14 perguntas que entrega ranking de carreiras, desafios esperados e seu próximo passo.
          </p>

          <button
            onClick={() => setShowConfirm(true)}
            className="w-full py-3.5 rounded-full text-sm font-semibold transition-all duration-200 hover:shadow-md"
            style={{
              fontFamily: "Work Sans, sans-serif",
              background: "#00DCA4",
              color: "#003326",
            }}
          >
            Iniciar diagnóstico
          </button>

          <p
            className="text-[11px] mt-5"
            style={{ fontFamily: "Work Sans, sans-serif", color: "#99A6AB" }}
          >
            Não há respostas certas ou erradas. Responda com honestidade.
          </p>
        </div>
      </motion.div>

      <AnimatePresence>
        {showConfirm && (
          <ConfirmModal
            onConfirm={onStart}
            onCancel={() => setShowConfirm(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
}
